/**
 * Global Property Service for Potree Integration
 *
 * This provides a singleton PropertyService instance that can be accessed
 * from anywhere in the application, including Potree code that doesn't have
 * access to React hooks.
 *
 * Usage in Potree:
 * ```typescript
 * import { getGlobalPropertyService } from '@pointorama/potree-shared';
 *
 * class SurfaceTool {
 *   init() {
 *     const service = getGlobalPropertyService();
 *     const tolerance = service.getProperty('surface_tolerance', this.surfaceId, PropertyType.PROJECT_GLOBAL);
 *
 *     // Subscribe to changes
 *     tolerance.subscribe(value => {
 *       this.updateIsolationPlane(value);
 *     });
 *
 *     // Direct access
 *     console.log(tolerance.value);
 *   }
 * }
 * ```
 */

import { PropertyService, PropertyServiceConfig, PropertyUpdate } from './PropertyService';
import { PropertyType, PropertyValueData } from '../types';

// Type for the broadcast function
type BroadcastPropertyUpdateFn = (identifier: string, objectIdentifier: string, propertyType: PropertyType, value: PropertyValueData) => void;
type BroadcastPropertyUpdateBatchFn = (updates: PropertyUpdate[]) => void;

// Store on window object so Potree bundle can access the same instance
interface GlobalPropertyWindow {
  __globalPropertyService?: PropertyService;
  __globalBroadcastPropertyUpdate?: BroadcastPropertyUpdateFn | null;
  __globalBroadcastPropertyUpdateBatch?: BroadcastPropertyUpdateBatchFn | null;
}
const globalWindow = window as unknown as GlobalPropertyWindow;

/**
 * Set the global broadcast function for property updates
 * This is called by useReactivePropertySync when it sets up the Pusher channel
 */
export function setGlobalBroadcastFunction(fn: BroadcastPropertyUpdateFn | null): void {
  globalWindow.__globalBroadcastPropertyUpdate = fn;
}

/**
 * Get the global broadcast function for property updates
 * Returns null if not initialized
 */
export function getGlobalBroadcastFunction(): BroadcastPropertyUpdateFn | null {
  return globalWindow.__globalBroadcastPropertyUpdate || null;
}

/**
 * Set the global batch broadcast function for property updates
 */
export function setGlobalBroadcastBatchFunction(fn: BroadcastPropertyUpdateBatchFn | null): void {
  globalWindow.__globalBroadcastPropertyUpdateBatch = fn;
}

/**
 * Get the global batch broadcast function for property updates
 */
export function getGlobalBroadcastBatchFunction(): BroadcastPropertyUpdateBatchFn | null {
  return globalWindow.__globalBroadcastPropertyUpdateBatch || null;
}

/**
 * Initialize the global property service
 * This should be called once during app initialization (e.g., in App.tsx or a provider)
 */
export function initializeGlobalPropertyService(config: PropertyServiceConfig): PropertyService {
  if (globalWindow.__globalPropertyService) {
    console.warn('Global PropertyService already initialized. Destroying old instance.');
    globalWindow.__globalPropertyService.destroy();
  }

  globalWindow.__globalPropertyService = new PropertyService(config);
  return globalWindow.__globalPropertyService as PropertyService;
}

/**
 * Get the global property service instance
 * Throws an error if not initialized
 */
export function getGlobalPropertyService(): PropertyService {
  if (!globalWindow.__globalPropertyService) {
    throw new Error('Global PropertyService not initialized. Call initializeGlobalPropertyService() first, typically in your App.tsx or root component.');
  }

  return globalWindow.__globalPropertyService as PropertyService;
}

/**
 * Check if global property service is initialized
 */
export function hasGlobalPropertyService(): boolean {
  return globalWindow.__globalPropertyService !== undefined;
}

/**
 * Destroy the global property service
 * Useful for cleanup or testing
 */
export function destroyGlobalPropertyService(): void {
  if (globalWindow.__globalPropertyService) {
    globalWindow.__globalPropertyService.destroy();
    globalWindow.__globalPropertyService = undefined;
  }
}
